import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { IonicSafeString, NavController } from '@ionic/angular';
import { RouteDetails, RouteMessage } from 'common/models/utilitymodels/base.model';
import { ToasterService } from './toaster.service';
import * as XLSX from 'xlsx';
import { StorageService } from './storage.service';
import { ExcludeRouteForExitTransaction, MonthNames, NavigateRoutes, RequestType, StatementType, StorageConstants, TransactionStatus } from 'common/constants/app.constant';
import { BehaviorSubject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DOCUMENT } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { Browser } from '@capacitor/browser';
import { Filesystem } from '@capacitor/filesystem';
import { LoadingService } from './loading.service';
import { GlobalErrorHandlerService } from './error-handler.service';
import { EncryptionDecryption } from 'common/utils/encrdecr.utility';
import { CustomError } from 'common/utils/error';
import SmartechBaseCordova from 'smartech-base-cordova';
import { SideMenuItem, SideMenuModel } from 'common/models/utilitymodels/menu.model';
import { CrossSellOpportuinityItem, CrossSellOpportunityModel } from 'common/models/pagemodels/Opportunity/cross-sell-opportunity.model';
import { ActivationOpportuinityItem, ActivationOpportunityModel } from 'common/models/pagemodels/Opportunity/activation-opportunity.model';
import { OpportuinityItem, OpportunityMetaModel } from 'common/models/pagemodels/Opportunity/opportunity.model';
import { OtherOpportuinityItem, OtherOpportunityModel } from 'common/models/pagemodels/Opportunity/other-opportunity.model';
import { SIPOpportuinityItem, SIPOpportunityModel } from 'common/models/pagemodels/Opportunity/sip-oppertunity.model';
import { CoBrandingApiQueryParams } from 'common/models/pagemodels/co-branding/co-branding.model';
import { QueryParams } from 'common/models/utilitymodels/list.model';

@Injectable({
    providedIn: 'root',
})
export class SystemService {
    deviceId: string = '';
    deviceInfo: any;
    loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
    openModal: BehaviorSubject<any> = new BehaviorSubject({ open: false, page: '' });
    showModal: boolean = false;
    isOnline: BehaviorSubject<boolean> = new BehaviorSubject(true);
    canNavigate: boolean = true;
    clientIP: string = '';
    ispendingTransctionUpdate: BehaviorSubject<string> = new BehaviorSubject(this.getLocalStorage('pendingTransactionCount') ?? '0');
    pendingTransactionCount: number = 0;
    routerDetails: RouteDetails | any = null;
    minimizedSideBar: boolean = false;
    isTransactionRouteForPopup: boolean = false;
    isCoBrandLoadRouteForPopup: boolean = false;
    isBrowser: boolean = false;
    logOutClicked: boolean = false;
    underMaintenance: boolean = false;
    isEditTransactionClick: boolean = false;
    isPriLoginTransaction: boolean = false;
    globalHandleError: GlobalErrorHandlerService;
    utmParams: any = {};

    // to bypass transaction popup
    bypassTrasactionPopup = false;
    currentYear: number | null = null;

    numberWords: { [key: number]: string } = {
        0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six',
        7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve',
        13: 'thirteen', 14: 'fourteen', 15: 'fifteen', 16: 'sixteen', 17: 'seventeen',
        18: 'eighteen', 19: 'nineteen', 20: 'twenty', 30: 'thirty', 40: 'forty',
        50: 'fifty', 60: 'sixty', 70: 'seventy', 80: 'eighty', 90: 'ninety'
    };

    constructor(
        private router: Router,
        private navController: NavController,
        private toaster: ToasterService,
        public storageService: StorageService,
        private modalService: NgbModal,
        @Inject(DOCUMENT) protected document: Document,
        @Inject(PLATFORM_ID) platformId: object
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
        this.currentYear = new Date().getFullYear();
    }

    public convertNumberToWords(num: number): string {
        if (num === 0) return 'zero';
        let result = '';
        if (num >= 10000000) {
            result += `${this.convertNumberToWords(Math.floor(num / 10000000))} crore `;
            num %= 10000000;
        }
        if (num >= 100000) {
            result += `${this.convertNumberToWords(Math.floor(num / 100000))} lakh `;
            num %= 100000;
        }
        if (num >= 1000) {
            result += `${this.convertNumberToWords(Math.floor(num / 1000))} thousand `;
            num %= 1000;
        }
        if (num >= 100) {
            result += `${this.convertNumberToWords(Math.floor(num / 100))} hundred `;
            num %= 100;
        }
        if (num > 0) {
            if (num <= 20) {
                result += `${this.numberWords[num]} `;
            } else {
                result += `${this.numberWords[Math.floor(num / 10) * 10]} `;
                if (num % 10 > 0) {
                    result += `${this.numberWords[num % 10]} `;
                }
            }
        }
        return result.trim();
    }

    async openExternalURL(path: string, event: any = null) {
        if (event) {
            event.preventDefault();
        }
        if (this.isBrowser) {
            if (!['web'].includes(this.deviceInfo.platform)) {
                await Browser.open({ url: path });
            } else {
                window.open(path, '_Blank');
            }
        }
    }

    routeByUrl(url: string, routeMessage: RouteMessage): Promise<Boolean> {
        return new Promise((resolve, reject) => {
            if (this.underMaintenance) {
                this.showToaster('We are under maintenance, Please Try Again later.');
                resolve(false);
            } else if (!this.bypassTrasactionPopup && this.isTransactionRouteForPopup && !this.isEditTransactionClick && !ExcludeRouteForExitTransaction.includes(url)) {
                this.showModal = true;
                this.routerDetails = {
                    url: url,
                    message: routeMessage,
                };
                resolve(false);
            } else if (this.isCoBrandLoadRouteForPopup) {
                this.showModal = true;
                this.routerDetails = {
                    url: url,
                    message: routeMessage,
                };
                resolve(false);
            } else {
                if (this.deviceInfo.platform === 'web' && routeMessage.type !== RequestType.BACK) {
                    this.removeSessionStorage(StorageConstants.currentPageState);
                    setTimeout(() => {
                        this.setSessionStorage(StorageConstants.currentPageState, JSON.stringify({ ...routeMessage, pageURL: url }));
                    }, 300);
                }
                this.router
                    .navigate([url], { queryParams: this.deviceInfo.platform === 'web' ? this.utmParams : {}, state: routeMessage, replaceUrl: true })
                    .then((res) => {
                        resolve(res);
                    })
                    .catch((err) => {
                        reject(err);
                    });
            }
        });
    }

    navigateBack() {
        this.navController.back();
    }

    showToaster(message: string | IonicSafeString, type: string = 'error', infinite: boolean = false) {
        this.toaster.showToaster(message, type, infinite);
    }

    // hideLoader() {
    //     this.loadingService.hideLoader();
    // }

    setSessionStorage(key: string, data: any) {
        sessionStorage.setItem(key, data);
    }

    getSesstionStorage(key: string) {
        const data: any = sessionStorage.getItem(key);
        return typeof data === 'string' ? data : JSON.parse(data);
    }

    encryptData(data: any) {
        const encrypted = new EncryptionDecryption().encrypt(data);
        return encrypted;
    }

    decryptData(data: any) {
        const decrypted = new EncryptionDecryption().decrypt(data);
        return decrypted;
    }

    encryptInBase64(data: string) {
        const encryptBase64 = new EncryptionDecryption().encryptInBase64(data);
        return encryptBase64;
    }

    setLocalStorage(key: string, data: any) {
        try {
            localStorage.setItem(key, typeof data === 'string' ? this.encryptData(data) : this.encryptData(JSON.stringify(data)));
        } catch (err: any) {
            throw new CustomError(err.message || 'An unexpected error occurred', err.status, 'INVALID_ENCRYPT');
        }
    }

    isValidJson(data_string: any) {
        try {
            JSON.parse(data_string); // Try to parse the string
            return true; // If no error, it's valid JSON
        } catch (e) {
            return false; // If error is thrown, it's not valid JSON
        }
    }

    getLocalStorage(key: string) {
        try {
            let data: any = localStorage.getItem(key);
            data = data ? this.decryptData(data) : data;
            if (/^"\{.*\}"$/.test(data)) {
                data = data.replace(/^"|"$/g, '"');
            } else if (/^".*"$/.test(data)) {
                data = data.replace(/^"|"$/g, '');
            }
            return typeof data === 'string' ? (this.isValidJson(data) ? JSON.parse(data) : data) : JSON.parse(data);
        } catch (err: any) {
            throw new CustomError(err.message || 'An unexpected error occurred', err.status, 'INVALID_ENCRYPT');
        }
    }

    removeLocalStorage(key: string) {
        localStorage.removeItem(key);
    }

    removeSessionStorage(key: string) {
        sessionStorage.removeItem(key);
    }

    formatDateForTransactionAPI(date: string) {
        if (date) {
            const dateObj = new Date(date);
            const day = dateObj.getDate();
            const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dateObj.getMonth()];
            const year = dateObj.getFullYear();
            return (day < 10 ? '0' + day : day) + '-' + month + '-' + year;
        } else {
            return null as any;
        }
    }

    getDateForFirstTime(dateStr: string) {
        if (!dateStr) return '';
        const split = dateStr.split('-');
        const temp = split[0];
        split[0] = split[1];
        split[1] = temp;
        const newDate = split.join('-');
        return new Date(newDate).toString();
    }

    replaceUndefinedOrNullWithEmptyString(inputObject: any): any {
        const result: any = {};

        for (const key in inputObject) {
            if (inputObject.hasOwnProperty(key)) {
                result[key] = inputObject[key] === undefined || inputObject[key] === null ? '' : inputObject[key];
            }
        }
        return result;
    }

    mergeFormValuesIntoTransactionRequest(formFields: any, formFeildValue: any): any {
        // Loop through the keys in formFields\

        const emptyReqObj: any = this.replaceUndefinedOrNullWithEmptyString(formFields);
        const formFeildRes: any = this.replaceUndefinedOrNullWithEmptyString(formFeildValue);
        const newReqObj: any = JSON.parse(JSON.stringify(emptyReqObj));
        for (const key in newReqObj) {
            const lowercaseKey = key.toLowerCase();
            // Check if the lowercase key exists in the request object
            if (formFeildRes.hasOwnProperty(key) && formFeildRes[key] !== '') {
                newReqObj[key] = formFeildRes[key];
            } else if (formFeildRes.hasOwnProperty(lowercaseKey) && formFeildRes[lowercaseKey] !== '') {
                newReqObj[key] = formFeildRes[lowercaseKey];
            }
        }
        return newReqObj;
    }

    mergeFormValuesIntoTransactionHistroy(formFields: any): any {
        // Loop through the keys in formFields\
        return Object.keys(formFields).reduce(function (newObj: any, key) {
            let val = formFields[key];

            let newVal = val;

            newObj[key.toLowerCase()] = newVal;

            return newObj;
        }, {});
    }

    validatePAN(panNumber: string) {
        var panPat = /^([a-zA-Z]{5})(\d{4})([a-zA-Z]{1})$/;
        if (panNumber.search(panPat) == -1) {
            return false;
        } else {
            return true;
        }
    }

    validateIFSC(ifsc: string) {
        var ifscPat = /^[A-Z]{4}0[A-Z0-9]{6}$/;
        if (ifsc.search(ifscPat) == -1) {
            return false;
        } else {
            return true;
        }
    }

    validateUPI(upi: string) {
        var upiPat = /^[0-9A-Za-z.-]{2,256}@[A-Za-z]{2,64}$/;
        if (upi.search(upiPat) == -1) {
            return false;
        } else {
            return true;
        }
    }

    dateReturnInNumber(date: string, type: 'normal' | 'pan' = 'normal'): string {
        if (date === undefined || date === '') {
            return '';
        }
        const inputDate = new Date(date);
        const day = inputDate.getDate();
        const month = inputDate.toLocaleString('en-us', { month: '2-digit' }); // Format month as '09'
        const year = inputDate.getFullYear();
        const formattedDay = day < 10 ? '0' + day : day.toString();
        return type === 'normal' ? `${year}-${month}-${formattedDay}` : `${formattedDay}-${month}-${year}`;
    }

    dateConversionPanVerify(date: string) {
        if (date.includes('/')) {
            const dobParts = date.split('/');
            const day = parseInt(dobParts[0], 10);
            const month = parseInt(dobParts[1], 10) - 1;
            const year = parseInt(dobParts[2], 10);
            const parsedDOB = new Date(year, month, day);
            const formatDob = ('0' + parsedDOB.getDate()).slice(-2) + '-' + ('0' + (parsedDOB.getMonth() + 1)).slice(-2) + '-' + parsedDOB.getFullYear();
            return formatDob;
        } else {
            const formatDob = this.dateReturnInNumber(date, 'pan');
            return formatDob;
        }
    }

    createEmptyObjectWithSameKeys(obj: any): any {
        const emptyObject: any = {};

        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                emptyObject[key] = '';
            }
        }

        return emptyObject;
    }

    alphaOnly(event: any) {
        var charCode = event.keyCode;
        if ((charCode > 64 && charCode < 91) || (charCode > 96 && charCode < 123) || charCode == 8) {
            return true;
        } else {
            return false;
        }
    }

    generateExcelBase64(data: any, Headers = []) {
        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
        const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
        const excelBase64: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'base64' });
        return excelBase64;
    }

    exportToExcel(data: any, fileName: string): void {
        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
        const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, fileName);
    }

    saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url: string = window.URL.createObjectURL(data);
        const a: HTMLAnchorElement = document.createElement('a');
        a.href = url;
        a.download = fileName + '.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
    }

    getYoutubeThumbnail(url: string): string {
        const thumbnail = `http://img.youtube.com/vi/${url}/0.jpg`;
        return thumbnail;
    }

    logOutPlexToken() {
        this.removeLocalStorage(StorageConstants.userProfile);
        this.removeLocalStorage(StorageConstants.kbhAccessToken);
        this.removeLocalStorage(StorageConstants.kbhRefreshToken);
        this.setLocalStorage(StorageConstants.loggedInCurrently, false);
        const routeMsg = new RouteMessage();
        routeMsg.type = RequestType.GET;
        this.routeByUrl(NavigateRoutes.welcome, routeMsg);
    }
    logOut() {
        if (!this.isTransactionRouteForPopup && !this.isCoBrandLoadRouteForPopup) {
            if (this.deviceInfo.platform !== 'web') {
                SmartechBaseCordova.logoutAndClearUserIdentity(false);
            }
            this.logOutClicked = false;
            this.removeLocalStorage(StorageConstants.userProfile);
            this.removeLocalStorage(StorageConstants.kbhAccessToken);
            this.removeLocalStorage(StorageConstants.kbhRefreshToken);
            this.removeSessionStorage(StorageConstants.zeroAUMValue);
            this.setLocalStorage(StorageConstants.loggedInCurrently, false);
            const routeMsg = new RouteMessage();
            routeMsg.type = RequestType.GET;
            this.routeByUrl(NavigateRoutes.welcome, routeMsg);
        } else {
            this.logOutClicked = true;
            this.showModal = true;
        }
    }

    isFutureDate(inputDate: string) {
        if (inputDate === '') {
            return false;
        }
        const currentDate = new Date();
        const givenDate = new Date(inputDate);

        return givenDate > currentDate;
    }

    getCurrentMonthInQuarter(date: Date) {
        let currentMonthInQuarter = 0;
        if ([0, 3, 6, 9].includes(date.getMonth())) {
            currentMonthInQuarter = 1;
        } else if ([1, 4, 7, 10].includes(date.getMonth())) {
            currentMonthInQuarter = 2;
        } else if ([2, 5, 8, 11].includes(date.getMonth())) {
            currentMonthInQuarter = 3;
        }
        return currentMonthInQuarter;
    }

    moveDateFromSATSUN(date: Date) {
        return date;
    }

    moveSATSUNinDailyCalculation(fromDate: any, days: any) {
        days = days - 1;
        var count = 0;
        while (count < days) {
            fromDate.setDate(fromDate.getDate() + 1);
            if (fromDate.getDay() != 0 && fromDate.getDay() != 6)
                // Skip weekends
                count++;
        }
        return fromDate;
    }

    calculateWeeklyStartDate(event: any, reqDate: Date = new Date()) {
        let date = reqDate;
        date = new Date(this.getNextDayOfTheWeek(event - 1, reqDate));
        const currentDayofWeek = new Date().getDay();

        const date1: any = new Date();
        const date2: any = date;
        const diffTime = Math.abs(date2 - date1);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays >= 4 || currentDayofWeek >= event) {
            date.setDate(date.getDate());
        } else {
            date.setDate(date.getDate() + 7);
        }
        return date;
    }

    getNextDayOfTheWeek(dayIndex: number, date: Date = new Date()) {
        var d = date;
        d.setDate(d.getDate() + ((dayIndex + 7 - d.getDay()) % 7));
        return d;
    }

    calculateWeecklyInstallmentEndDate(startDate: Date, installmentNumber: number) {
        // Clone the start date to avoid modifying the original date
        let endDate = startDate;

        // Calculate the number of days to add based on the installment number
        endDate.setDate(endDate.getDate() + (installmentNumber - 1) * 7);
        return endDate;
    }

    getShortFormatDate = function (date: Date) {
        const day = date.getDate();
        const monthIndex = date.getMonth();
        const monthName = MonthNames[monthIndex];
        const year = date.getFullYear();
        return `${day >= 10 ? day : '0' + day}-${monthName}-${year}`;
    };

    getDateObject(date: Date): { year: number; month: number; day: number; } {
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // Note: January is 0, so we add 1
        const day = date.getDate();

        return { year, month, day };
    }

    hideMobileNumberEmail(number: string) {
        return number ? number.replace(/^.{5}/g, '*****') : '';
    }

    backButtonNavigate(routeUrl: string): Promise<any> {
        return new Promise((resolve, reject) => {
            let routeUrlFromStorage = this.storageService.getLastKeyAndDeletefromStorage();

            const routeData = new RouteMessage();
            this.storageService
                .getStorage(routeUrlFromStorage)
                .then((data: any) => {
                    if (data) {
                        routeUrl = routeUrlFromStorage;
                        routeData.type = RequestType.BACK;
                        routeData.transferData = data;
                    } else {
                        if (routeUrl == NavigateRoutes.preLoginTransaction && this.getSesstionStorage(StorageConstants.isPreLoginInitiat) === 'true') {
                            routeData.type = RequestType.BLANK;
                            routeData.transferData = null;
                        } else if (routeUrl !== NavigateRoutes.welcome) {
                            routeUrl = NavigateRoutes.distributordashboard;
                            routeData.type = RequestType.ID;
                        } else {
                            routeUrl = NavigateRoutes.welcome;
                            routeData.type = RequestType.GET;
                        }
                    }
                    resolve({ routeUrl, routeData });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    getDiffBetweenTwoDates(date1: any, date2: any) {
        const diffTime = Math.abs(date2 - date1);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    }

    scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const scrollEl = document.querySelector('.app-router-outlet');
        if (scrollEl !== null) {
            scrollEl.scrollTo(0, 0);
        }
    }

    dismissPopup() {
        this.showModal = false;

        document.body.classList.remove('hide-modal-body-overflow');
        this.modalService.dismissAll();
    }

    scrollToSection(id: string, offSet = 0) {
        const el = this.document.getElementById(id);
        this.document.documentElement.scrollTop = 0;
    }

    getAge(DOB: any) {
        var today = new Date();
        var birthDate = new Date(DOB);
        var age = today.getFullYear() - birthDate.getFullYear();
        var m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    }

    async getFilesystemPermissions() {
        return await Filesystem.checkPermissions();
    }

    emptyAllKeyObject(object: any) {
        Object.keys(object).forEach((item) => {
            object[item] = '';
        });
        return object;
    }

    getSchemeOptionValue(option: string) {
        let retValue = '';
        switch (option) {
            case 'Not Applicable':
                retValue = 'Z';
                break;
            case 'Reinvest':
                retValue = 'Y';
                break;
            case 'Payout':
                retValue = 'N';
                break;
            default:
                break;
        }
        return retValue;
    }

    clearCartData() {
        // if (this.getSesstionStorage(StorageConstants.lumpsumMetaData)) {
        //     this.removeSessionStorage(StorageConstants.lumpsumMetaData);
        // }
        // if (this.getSesstionStorage(StorageConstants.sipMetaData)) {
        //     this.removeSessionStorage(StorageConstants.sipMetaData);
        // }
        if (this.getSesstionStorage(StorageConstants.lumpsumCart)) {
            this.removeSessionStorage(StorageConstants.lumpsumCart);
        }
        if (this.getSesstionStorage(StorageConstants.sipCart)) {
            this.removeSessionStorage(StorageConstants.sipCart);
        }
        this.storageService.removeStorageItem(StorageConstants.lumpsumMetaData);
        this.storageService.removeStorageItem(StorageConstants.sipMetaData);
    }

    updateOpenGraphMetaTags(title: string, description: string, image: string): void {
        const ogTitleTag = document.querySelector('meta[property="og:title"]');
        const ogDescriptionTag = document.querySelector('meta[property="og:description"]');
        const ogImageTag = document.querySelector('meta[property="og:image"]');

        if (ogTitleTag && ogDescriptionTag && ogImageTag) {
            ogTitleTag.setAttribute('content', title);
            ogDescriptionTag.setAttribute('content', description);
            ogImageTag.setAttribute('content', image);
        } else {
            console.error('Open Graph meta tags not found.');
        }
    }

    isValidEUIN(euin: string) {

        if (euin === undefined || euin === '' || euin === null) {
            return true;
        }
        if (euin.length !== 7) {
            return false;
        }
        const euinRegex = /^E\d{6,}$/;
        return euinRegex.test(euin);
    }



    getFinancialYear(date = new Date()) {
        let year = date.getFullYear();
        let month = date.getMonth() + 1; // JavaScript months are 0-based

        // If the month is before April (1 - Jan, 2 - Feb, 3 - Mar), it's part of the previous financial year
        if (month < 4) {
            return `${year - 1}`;
        } else {
            return `${year}`;
        }
    }

    formatDateWithSlash(inputDate: string) {
        if (!inputDate) return '';
        const [day, month, year] = inputDate.split("/").map(Number);
        const date = new Date(year, month - 1, day);

        const options: any = { day: '2-digit', month: 'short', year: 'numeric' };
        let newDate = date.toLocaleDateString('en-GB', options).replace(" ", "-");
        return newDate.replace(" ", "-");
    }

    async navigateInternally(url: string) {
        const sideMenuInfo: SideMenuItem[] = new SideMenuModel().sideMenuInfo;
        let isNavigate: boolean = true;
        let navigationDtls = null;
        const result = this.setRestrictedRouteNavigation(url);
        if (result !== null) {
            navigationDtls = result;
        }
        if (navigationDtls === null) {
            const subLink = url.substring(0, url.lastIndexOf('/'));

            for (let routes of sideMenuInfo) {
                if (routes.subItems.length > 0) {
                    for (let subItems of routes.subItems) {
                        if (isNavigate && subItems.isdeepLink && subItems.url === url) {
                            navigationDtls = subItems;
                        } else if (isNavigate && subItems.isdeepLink && subItems.url === subLink) {
                            navigationDtls = subItems;
                        }
                    }
                }
                if (isNavigate && navigationDtls === null && routes.url === url) {
                    navigationDtls = routes;
                } else if (isNavigate && navigationDtls === null && routes.url === subLink) {
                    navigationDtls = routes;
                }
            }
        }
        if (navigationDtls !== null) {
            this.openInternalPage(navigationDtls);
        }
    }

    async openInternalPage(item: SideMenuItem) {
        if (!this.canNavigate) {
            this.showToaster('You are Offline!', 'error');
            return;
        }
        const routeMessage = new RouteMessage();
        routeMessage.transferData = item;
        let url = item.url;
        if (item.url.startsWith(NavigateRoutes.accountStatement) || item.url.startsWith(NavigateRoutes.capitalGainStatement)) {
            url = NavigateRoutes.selectInvestor;
            if (item.url.startsWith(NavigateRoutes.accountStatement)) {
                url = url + '/account-statement';
            } else if (item.url.startsWith(NavigateRoutes.capitalGainStatement)) {
                url = url + '/capital-gain-statement';
            }
            routeMessage.type = RequestType.GET;
        } else if (item.url.startsWith('/distributor/investor')) {
            routeMessage.type = RequestType.GET;
            routeMessage.transferData = item?.routerData ? item.routerData : {};
        } else if (item.url.startsWith(NavigateRoutes.coBranding)) {
            item.routerData.apiParams = new CoBrandingApiQueryParams();
            routeMessage.transferData = item;
            routeMessage.type = item.requestType;
        } else if (item.url === NavigateRoutes.transactionHistory) {
            routeMessage.type = RequestType.GET;
            routeMessage.transferData = new QueryParams();
            routeMessage.transferData.apiParams.status = TransactionStatus.all;
            routeMessage.transferData.filterValue = TransactionStatus.all;
        } else {
            routeMessage.type = item.requestType;
        }
        this.routeByUrl(url, routeMessage);
    }



    getUrlandUpdateLink(item: SideMenuItem) {
        if (!this.canNavigate) {
            this.showToaster('You are Offline!', 'error');
            return;
        }
        const routeMessage = new RouteMessage();
        routeMessage.transferData = item;
        let url = item.url;
        if (item.url.startsWith(NavigateRoutes.accountStatement) || item.url.startsWith(NavigateRoutes.capitalGainStatement)) {
            url = NavigateRoutes.selectInvestor;
            if (item.url.startsWith(NavigateRoutes.accountStatement)) {
                url = url + '/account-statement';
            } else if (item.url.startsWith(NavigateRoutes.capitalGainStatement)) {
                url = url + '/capital-gain-statement';
            }
            routeMessage.type = RequestType.GET;
        } else if (item.url.startsWith('/distributor/investor')) {
            routeMessage.type = RequestType.GET;
            routeMessage.transferData = item?.routerData ? item.routerData : {};
        } else if (item.url.startsWith(NavigateRoutes.coBranding)) {
            item.routerData.apiParams = new CoBrandingApiQueryParams();
            routeMessage.transferData = item;
            routeMessage.type = item.requestType;
        } else if (item.url === NavigateRoutes.transactionHistory) {
            routeMessage.type = RequestType.GET;
            routeMessage.transferData = new QueryParams();
            routeMessage.transferData.apiParams.status = TransactionStatus.all;
            routeMessage.transferData.filterValue = TransactionStatus.all;
        } else {
            routeMessage.type = item.requestType;
        }

        return {
            redirectUrl: url, message: routeMessage
        };

    }






    setRestrictedRouteNavigation(url: string = '') {
        if (url.includes('opportunity')) {
            this.onOpportunityDeepLink(url);
            return null;
        } else if (url.endsWith('/transact/investor-list/account-statement')) {
            return {
                id: 1,
                name: 'Account Statement',
                url: NavigateRoutes.accountStatement,
                routerData: {
                    statement: true,
                    typeofStatement: StatementType.account,
                },
                subItems: [],
                requestType: RequestType.ID,
                selected: false,
                show: true,
                isdeepLink: true,
            };
        } else if (url.endsWith('/transact/investor-list/capital-gain-statement')) {
            return {
                id: 2,
                name: 'Capital Gain Statement',
                url: NavigateRoutes.capitalGainStatement,
                routerData: {
                    statement: true,
                    typeofStatement: StatementType.capitalGain,
                },
                subItems: [],
                requestType: RequestType.ID,
                selected: false,
                show: true,
                isdeepLink: true,
            };
        } else {
            return null;
        }
    }

    async onOpportunityDeepLink(url: string) {
        let oppData: any = [];
        const sipOppData: SIPOpportuinityItem[] = new SIPOpportunityModel().listInfo;
        const crossOppData: CrossSellOpportuinityItem[] = new CrossSellOpportunityModel().listInfo;
        const activeOppData: ActivationOpportuinityItem[] = new ActivationOpportunityModel().listInfo;
        const otherOppData: OtherOpportuinityItem[] = new OtherOpportunityModel().listInfo;
        const oppDataList: OpportuinityItem[] = new OpportunityMetaModel().OpportuinityInfo;

        oppData = [...sipOppData, ...crossOppData, ...activeOppData, ...otherOppData, ...oppDataList];
        for (let sipOpp of oppData) {
            if (sipOpp.url === url) {
                const modifyOpp = sipOpp;
                modifyOpp.deepLink = true;
                const routeMsg = new RouteMessage();
                routeMsg.type = ['SIP Opportunities', 'CROSS SELL OPPORTUNITIES', 'ACTIVATION OPPORTUNITIES', 'OTHER OPPORTUNITIES'].includes(sipOpp.name) ? RequestType.ID : RequestType.GET;
                routeMsg.transferData = modifyOpp;
                this.routeByUrl(url, routeMsg);
            }
        }
    }


    getRouteDetailsFromUrl(url: string): any {

        this.setSessionStorage(StorageConstants.redirectionUrl, url);
        let newUrl: string = '';
        let urlTransform: string[] = url.split('?');
        let routeMessage: any | RouteMessage = null;
        const sideMenuInfo: SideMenuItem[] = new SideMenuModel().sideMenuInfo;
        let isdeepLinkFromSideMenu: boolean = false;
        url = urlTransform[0];
        const slugUrl = urlTransform[1] ? urlTransform[1] : '';
        const subLink = url.substring(0, url[0].lastIndexOf('/'));
        for (let routes of sideMenuInfo) {
            if (routes.subItems.length > 0) {
                for (let subItems of routes.subItems) {
                    if (subItems.isdeepLink && subItems.url === url) {
                        routeMessage = subItems;
                    } else if (subItems.isdeepLink && subItems.url === subLink) {
                        routeMessage = subItems;
                    }
                }
            }
            if (routeMessage === null && routes.url === url) {
                routeMessage = routes;
            } else if (routeMessage === null && routes.url === subLink) {
                routeMessage = routes;
            }
        }
        newUrl = url;

        if (!this.getLocalStorage(StorageConstants.userProfile) && url === NavigateRoutes.preLoginTransaction && this.deviceInfo.platform === 'web') {
            routeMessage = new RouteMessage();
            routeMessage.type = RequestType.BLANK;
            routeMessage.transferData = null;
        } else if (routeMessage !== null) {
            isdeepLinkFromSideMenu = true;
        }

        if (url.includes('opportunity') && routeMessage === null) {
            const newRouteMessage = this.onOpportunityDeepLinkForUrl(url);
            newUrl = url;
            routeMessage = newRouteMessage;
        } else if (url.endsWith('/transact/investor-list/account-statement') && routeMessage === null) {
            const newRouteMessage = {
                id: 1,
                name: 'Account Statement',
                url: NavigateRoutes.accountStatement,
                routerData: {
                    statement: true,
                    typeofStatement: StatementType.account,
                },
                subItems: [],
                requestType: RequestType.ID,
                selected: false,
                show: true,
                isdeepLink: true,
            };
            newUrl = NavigateRoutes.accountStatement;
            routeMessage = newRouteMessage;
            isdeepLinkFromSideMenu = true;
        } else if (url.endsWith('/transact/investor-list/capital-gain-statement') && routeMessage === null) {
            const newRouteMessage = {
                id: 2,
                name: 'Capital Gain Statement',
                url: NavigateRoutes.capitalGainStatement,
                routerData: {
                    statement: true,
                    typeofStatement: StatementType.capitalGain,
                },
                subItems: [],
                requestType: RequestType.ID,
                selected: false,
                show: true,
                isdeepLink: true,
            };
            newUrl = NavigateRoutes.capitalGainStatement;
            routeMessage = newRouteMessage;
            isdeepLinkFromSideMenu = true;
        } else if (url.startsWith(NavigateRoutes.coBrandingDetail) || url.startsWith(NavigateRoutes.coBrandingLoading) && routeMessage === null) {
            const coBrandId = url.split('/').reverse()[0];
            const newRouteMessage = new RouteMessage();
            newRouteMessage.type = RequestType.ID;
            newRouteMessage.transferData = { id: coBrandId };
            newUrl = NavigateRoutes.coBrandingLoading + coBrandId;
            console.log("new url", newUrl);
            routeMessage = newRouteMessage;
        }

        // This is the code to redirect view cirtificate 
        else if (url.startsWith(NavigateRoutes.veiwcertificate) && routeMessage === null) {
            const newRouteMessage = new RouteMessage();
            newRouteMessage.type = RequestType.ID;
            newRouteMessage.transferData = slugUrl;
            // const queryParams = this.activatedRoute.snapshot.queryParams;
            newUrl = url + '?' + slugUrl;
            routeMessage = newRouteMessage;
        }

        //this is the code pre logn co-branding Page
        else if (url.startsWith(NavigateRoutes.viewContent) && routeMessage === null) {
            const newRouteMessage = new RouteMessage();
            newRouteMessage.type = RequestType.ID;
            newUrl = url;
            const urlNew: any = url.split('/').reverse();
            newRouteMessage.transferData = {
                userId: urlNew[0],
                contentId: urlNew[1],
            };

            routeMessage = newRouteMessage;
        }
        return isdeepLinkFromSideMenu ? this.getUrlandUpdateLink(routeMessage) : {
            redirectUrl: newUrl, message: routeMessage
        };
    }

    onOpportunityDeepLinkForUrl(url: string): any {
        let oppData: any = [];
        const sipOppData: SIPOpportuinityItem[] = new SIPOpportunityModel().listInfo;
        const crossOppData: CrossSellOpportuinityItem[] = new CrossSellOpportunityModel().listInfo;
        const activeOppData: ActivationOpportuinityItem[] = new ActivationOpportunityModel().listInfo;
        const otherOppData: OtherOpportuinityItem[] = new OtherOpportunityModel().listInfo;
        const oppDataList: OpportuinityItem[] = new OpportunityMetaModel().OpportuinityInfo;

        oppData = [...sipOppData, ...crossOppData, ...activeOppData, ...otherOppData, ...oppDataList];
        for (let sipOpp of oppData) {
            if (sipOpp.url === url) {
                const modifyOpp = sipOpp;
                modifyOpp.deepLink = true;
                const routeMsg = new RouteMessage();
                routeMsg.type = ['SIP Opportunities', 'CROSS SELL OPPORTUNITIES', 'ACTIVATION OPPORTUNITIES', 'OTHER OPPORTUNITIES'].includes(sipOpp.name) ? RequestType.ID : RequestType.GET;
                routeMsg.transferData = modifyOpp;
                return routeMsg;
            }
        }

    }



}
